import { ApiError } from './authService'

export interface MutationResponse<T = unknown> {
  success: boolean
  message: string
  data?: T
}

export interface DiscordVerificationResponse {
  code: string
  expiresAt: string
}

export interface EconomyActivity {
  id: number
  activityType: string
  xpEarned: number
  coinsEarned: number
  timestamp: string
  streak?: number
  workType?: string
}

async function apiRequest<T>(url: string, options?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  })

  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: res.statusText }))
    throw new ApiError(res.status, error.message || 'Request failed')
  }

  const text = await res.text()
  if (!text) return {} as T
  return JSON.parse(text)
}

export async function changePassword(oldPassword: string, newPassword: string): Promise<MutationResponse> {
  return apiRequest<MutationResponse>('/api/users/me/password/change', {
    method: 'POST',
    body: JSON.stringify({ oldPassword, newPassword }),
  })
}

export async function setPassword(newPassword: string): Promise<MutationResponse> {
  return apiRequest<MutationResponse>('/api/users/me/password/set', {
    method: 'POST',
    body: JSON.stringify({ newPassword }),
  })
}

export async function linkEmail(email: string, password: string): Promise<MutationResponse<{ userId: number; email: string }>> {
  return apiRequest<MutationResponse<{ userId: number; email: string }>>('/api/users/me/email', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  })
}

export async function linkDiscord(discordId: string): Promise<MutationResponse<{ discordId: string }>> {
  return apiRequest<MutationResponse<{ discordId: string }>>('/api/users/me/discord', {
    method: 'POST',
    body: JSON.stringify({ discordId }),
  })
}

export async function requestDiscordVerification(): Promise<DiscordVerificationResponse> {
  return apiRequest<DiscordVerificationResponse>('/api/users/me/discord/verification', {
    method: 'POST',
  })
}

export async function fetchEconomyActivities(userId: number): Promise<EconomyActivity[]> {
  return apiRequest<EconomyActivity[]>(`/api/users/${userId}/economy-activities`)
}

export async function setUsername(username: string): Promise<MutationResponse<{ username: string }>> {
  return apiRequest<MutationResponse<{ username: string }>>('/api/users/me/username', {
    method: 'POST',
    body: JSON.stringify({ username }),
  })
}
